/** The service rung (moved from `builder/rows.tsx` at step 8). */
import { Children, type ReactNode } from 'react'
import { cn } from '../../lib/cn.js'
import { IconChevronDown, IconChevronRight } from '../../frame/icons.js'
import { InternalBand, lineGrid, useLineGrid } from './grid.js'
import { RowMenuButton, type MenuAt } from './add-row.js'

// ---------------------------------------------------------------------------
// The service
// ---------------------------------------------------------------------------

export interface ServiceShellProps {
  name: string
  onEditName?: () => void
  /** The division tag, and whatever else names this rung. */
  tags?: ReactNode
  /** Between the name and the right-hand numbers — the measure chip. */
  after?: ReactNode
  collapsed?: boolean
  onToggle?: () => void
  onMenu?: (at: MenuAt) => void
  /** The "+ Material + Labor …" strip under the lines. */
  addRow?: ReactNode
  /**
   * The columns, in the order the header names them (folder 44 §5.2): the
   * measure spanning Qty and Unit, the total under Price, and ours on the band.
   */
  columns: { measure?: ReactNode; price: ReactNode; cost: ReactNode; margin: ReactNode }
  /** A second line under the name: the division and the hours. */
  sub?: ReactNode
  children?: ReactNode
}

export function ServiceShell({
  name, onEditName, tags, after, collapsed, onToggle, onMenu, addRow, columns, sub, children,
}: ServiceShellProps) {
  const { mode, qty } = useLineGrid()
  return (
    <>
      {/*
        * The middle rung. Its circle sits 12px in from the visit's tile and
        * 12px out from a line's, so the three read as a hierarchy down the
        * left edge: the visit, the service on it, the things it is made of.
        * The lines used to sit *further left* than the service above them,
        * which drew the tree upside down.
        */}
      <div className={cn(lineGrid(mode, qty),
        'border-t border-line bg-surface-alt py-2.5 pl-6 pr-3.5')}>
        {onToggle
          ? (
            <button
              type="button"
              onClick={onToggle}
              aria-label={collapsed ? `Show ${name}` : `Hide ${name}`}
              aria-expanded={!collapsed}
              className="flex h-5 w-5 items-center justify-center text-text-faint hover:text-text"
            >
              {collapsed ? <IconChevronRight size={14} /> : <IconChevronDown size={14} />}
            </button>
          )
          : <span />}

        <div className="flex min-w-0 items-center gap-2.5">
          <ServiceCircle />
          {sub !== undefined
            ? (
              <span className="flex min-w-0 flex-col gap-[3px]">
                <span className="flex min-w-0 items-center gap-2.5">
                  <ServiceName name={name} onEditName={onEditName} />
                  {after}
                </span>
                <span className="flex min-w-0 items-center gap-[7px]">{tags}{sub}</span>
              </span>
            )
            : null}
          {/*
            * A floor under the name. Everything beside it is `shrink-0`, so
            * the name was the only thing that could give — and on a service
            * carrying a division tag and a measure chip it gave all the way
            * down to "B.". A name truncated to one letter is not a name.
            */}
          {sub === undefined && (
            <>
              <ServiceName name={name} onEditName={onEditName} />
              {tags}
              {after}
            </>
          )}
        </div>

        <span className="col-span-2 flex min-w-0 justify-start">{columns.measure}</span>
        {mode === 'all' && <span />}
        {columns.price}
        <InternalBand inset="-my-2.5" alt cost={columns.cost} margin={columns.margin} />
        {onMenu && <RowMenuButton label={`${name} options`} onOpen={onMenu} small />}
      </div>

      {!collapsed && (
        <>
          {children}
          {/*
            * The hairline between the last line and the add links (Aaron,
            * item 3.1). Every line draws the rule *above* itself, so the last
            * one had nothing under it and ran straight into "+ Materials".
            * Only when there are lines: with none, the links sit directly
            * under the service's tinted rung and its edge is the separation
            * already — a rule there would be a second one.
            */}
          {addRow && (Children.toArray(children).length > 0
            ? <div data-add-row-rule="" className="border-t border-line-soft">{addRow}</div>
            : addRow)}
        </>
      )}
    </>
  )
}

function ServiceName({ name, onEditName }: { name: string; onEditName?: () => void }) {
  return onEditName
    ? (
      <button
        type="button"
        data-service-name=""
        onClick={onEditName}
        className="min-w-[7rem] truncate text-left text-[13.5px] font-bold text-text hover:text-accent"
      >
        {name}
      </button>
    )
    : <b className="min-w-[7rem] truncate text-[13.5px] font-bold text-text">{name}</b>
}

/**
 * The service mark — 32px, white, one hairline, the layers glyph.
 *
 * `design-system.md` §4.2 makes this a circle rather than a tinted square on
 * purpose: a service is not a kind of thing you can buy, it is the container
 * the things you buy sit in, and giving it the same tile as a material said
 * they were siblings.
 */
function ServiceCircle() {
  return (
    <span
      aria-hidden
      className={cn('flex h-8 w-8 shrink-0 items-center justify-center rounded-full',
        'border border-line bg-surface text-text')}
    >
      <svg
        viewBox="0 0 24 24" width={16} height={16} fill="none" stroke="currentColor"
        strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round"
      >
        <path d="M12 3l9 5-9 5-9-5z" /><path d="M3 13l9 5 9-5" />
      </svg>
    </span>
  )
}
